import {
  ACCOUNT_SETTINGS_SECTIONS,
  type AccountCopy,
  type AccountSettingsSection,
  defaultAccountCopy,
  type PublicShippingDestination,
  type ShippingAddress,
} from "@loot-os/api-contract";
import type {
  AccountContent,
  AccountNoticesContent,
  AccountTabsContent,
} from "@loot-os/design-system/templates/account/accountContent";
import { useState } from "react";

import { useCurrency } from "../../../lib/currency.js";
import { shippingDestinationsOf, useShippingAddress } from "../../../lib/shippingAddress.js";
import { useTenant } from "../../../lib/tenant.js";
import { useTerminology } from "../../../lib/terminology.js";
import {
  useAccountHeaderContent,
  useAccountTabsContent,
  useLootMe,
} from "../shared/accountHeader.js";
import { useAdvancedContent } from "./content/advanced.js";
import { useHistoryContent } from "./content/history.js";
import { useProfileContent } from "./content/profile.js";
import { accountHref, useAccountView } from "./content/search.js";
import { useShippingContent } from "./content/shipping.js";
import { useVerificationContent } from "./content/verification.js";

/** The dialogs the Account page opens over its template, each the app's own adapter. */
export interface AccountDialogs {
  username: { open: boolean; setOpen: (open: boolean) => void; username: string | null } | null;
  address: {
    open: boolean;
    setOpen: (open: boolean) => void;
    address: ShippingAddress | null;
    destinations: PublicShippingDestination[];
  };
}

/**
 * The Account page: GET /v1/me, /v1/me/balance, /v1/me/opens, /v1/me/battles,
 * /v1/me/shipping-address and PUT /v1/me/settings, the session-token surface. Buying and gift
 * codes are Add funds' (`lib/depositContent.ts`), which the header's Deposit opens. Every section's content, the same under every template, with only the
 * tab showing filled; plus the dialogs its actions open. The words are the store's default copy
 * (`defaultAccountCopy`, with `defaultAccountHeaderCopy` for the header and tabs); the lock
 * notice, the failed saves and the Verification panel are the platform's.
 */
export function useAccountPage(): { content: AccountContent; dialogs: AccountDialogs } {
  const { config } = useTenant();
  const currency = useCurrency();
  const terms = useTerminology();
  const copy = defaultAccountCopy(terms);
  const me = useLootMe();
  const { tab, section } = useAccountView(config.kycRequired);
  const [error, setError] = useState<string | null>(null);
  const [usernameOpen, setUsernameOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);

  // GET /v1/me carries the lock; the server refuses opens, claims, and balance purchases on its
  // own, so this is the shopper-facing half of the same state.
  const locked = me?.locked ?? false;
  const destinations = shippingDestinationsOf(config);
  const shippingAddress = useShippingAddress();

  const header = useAccountHeaderContent(me);
  const tabs = useAccountTabsContent(tab);
  const profile = useProfileContent(me, () => setUsernameOpen(true));
  const shipping = useShippingContent({
    destinations,
    address: shippingAddress,
    locked,
    openAddress: () => setAddressOpen(true),
  });
  const verification = useVerificationContent({ me, locked, setError });
  const advanced = useAdvancedContent({ me, setError });
  const history = useHistoryContent({ me, active: tab === "history" });

  const notices: AccountNoticesContent["notices"] = [
    ...(locked
      ? [
          {
            id: "locked",
            tone: "danger" as const,
            title: "This account is locked.",
            announce: false,
            text: `You can't open ${terms.box.plural}, claim reveals, or buy ${currency.namePlural} while the lock is in place. Contact support to get it lifted.`,
          },
        ]
      : []),
    ...(error ? [{ id: "error", tone: "danger" as const, text: error }] : []),
  ];

  const settings = tab === "settings";
  const shows = (panel: AccountSettingsSection) => settings && section === panel;
  const content: AccountContent = {
    header,
    tabs,
    notices: { notices },
    settingsNav: settings ? settingsNav(section, config.kycRequired, copy.settingsNav) : undefined,
    profile: shows("profile") ? profile : undefined,
    shipping: shows("shipping") ? shipping : undefined,
    verification: shows("verification") && config.kycRequired ? verification : undefined,
    advanced: shows("advanced") ? advanced : undefined,
    history: tab === "history" ? history : undefined,
  };

  return {
    content,
    dialogs: {
      username: me ? { open: usernameOpen, setOpen: setUsernameOpen, username: me.username } : null,
      address: {
        open: addressOpen,
        setOpen: setAddressOpen,
        address: shippingAddress.data?.shippingAddress ?? null,
        destinations,
      },
    },
  };
}

/** Settings' panels, Verification only where the store verifies identity. */
function settingsNav(
  current: AccountSettingsSection,
  kycRequired: boolean,
  copy: AccountCopy["settingsNav"],
): AccountTabsContent {
  const panels = ACCOUNT_SETTINGS_SECTIONS.filter((id) => kycRequired || id !== "verification");
  return {
    label: copy.label,
    items: panels.map((id) => ({
      id,
      label: copy.items[id],
      href: accountHref("settings", id),
      current: id === current,
    })),
  };
}
